import React, { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { UserContext } from '../context/UserContext';
import { useProjectStore, useProjectStoreBase, type Project } from '../stores/projectStore';
import { ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, BulkSelectCell, RowActions, RowAction, StatusDot, ListPagination, ListSearchInput, ListEmptyRow, BulkSelectHeader, MultiBulkActionButton, btnPrimary, btnDisabled } from '@redpanda/ui';
import { projectTone, toneOf } from '../utils/statusTone';
import { formatTableDate } from '@redpanda/utils';
import ProjectDialog from '../components/ProjectDialog';
import CustomerLimitBanner from '../components/CustomerLimitBanner';
import { useCustomerLimitCache } from '../hooks/useCustomerLimitCache';
import { bulkDeleteProjects, getProjects } from '../services/api';
import { useBulkActions, isAdmin } from '@redpanda/utils';
import { canModify } from '../utils/permissions';
import { notifyDataChanged } from '../utils/dataEvents';
import { useListSearch } from '../hooks/useListSearch';
import { pendingRowClass } from '../utils/pendingItem';
import { createBulkDeleteOptimisticHandler, createBulkDeleteSuccessHandler } from '@redpanda/utils';
import { TableSkeleton } from '@redpanda/ui';
import { getCustomerStatus, getArchivedRowClassName } from '../utils/customerUtils';


const ProjectListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const userContext = useContext(UserContext);
  const { isAuthenticated, user, isLoading: isUserLoading } = userContext!;
  const currentUserId = user?.id;
  const userRole = user?.role;
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  
  // 使用客戶限制快取 hook
  const {
    canAddCustomer,
    setCanAddCustomer,
    limitBannerRefreshTrigger,
  } = useCustomerLimitCache();

  const {
    projects,
    pagination,
    error,
    isPageLoading,
    showDeleteConfirmModal,
    isDeleting,
    setCurrentPage,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
    fetchProjects,
  } = useProjectStore();

  // 搜尋由後端查全部專案；沒有搜尋時用列表 store
  const [searchTerm, setSearchTerm] = useState('');
  const search = searchTerm.trim();
  const searchResult = useListSearch<Project>('projects', search ? { search } : null, page => getProjects(page, { search }));
  const listItems = searchResult.active ? searchResult.items : projects;
  const listPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;

  const bulkDeleteSuccess = createBulkDeleteSuccessHandler(() => useProjectStoreBase.getState(), fetchProjects, setCurrentPage);

  // 使用新的批次操作 hook
  const {
    selectedItems: selectedProjects,
    showBulkDeleteModal,
    isBulkDeleting,
    isItemSelected,
    allSelected,
    toggleItemSelection,
    toggleSelectAll,
    handleBulkDelete,
    confirmBulkDelete,
    closeBulkDeleteModal,
    canDelete,
    handlePageChange
  } = useBulkActions({
    items: listItems,
    idField: 'id',
    entityName: t('projects.item', { ns: 'pages' }),
    bulkDeleteApi: bulkDeleteProjects,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(
      useProjectStoreBase.setState,
      'project',
      () => projects,
      () => pagination
    ),
    onSuccess: async (deletedIds?: string[]) => {
      await bulkDeleteSuccess(deletedIds ?? []);
      notifyDataChanged('projects');
    },
    userRole,
    t,
    toast,
    // 智能分頁配置
    pagination: pagination ? {
      currentPage: pagination.currentPage,
      totalPages: pagination.totalPages
    } : undefined,
    fetchItems: fetchProjects,
    setCurrentPage
  });

  // 使用 ref 來穩定化函數引用，避免無限循環
  const fetchProjectsRef = useRef(fetchProjects);
  fetchProjectsRef.current = fetchProjects;

  useEffect(() => {
    if (isAuthenticated && currentUserId) {
      // 頁面初始化時使用快取（如果有），避免每次切換都重新載入
      fetchProjectsRef.current(1, false);
    }
  }, [isAuthenticated, currentUserId]);

  // 搜尋結果換頁走搜尋；否則走列表 store（清除勾選）
  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : handlePageChange(page));

  const handleAddProject = () => {
    setSelectedProject(null);
    setIsDialogOpen(true);
  };

  const handleEditProject = (project: any) => {
    setSelectedProject(project);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedProject(null);
  };


  if (isUserLoading) {
    return null;
  }

  if (!isAuthenticated || !currentUserId) {
    return <div className="p-8 text-center text-red-500">{t('common.messages.unauthorized')}</div>;
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('projects.list.title', { ns: 'pages' })}</h1>
          <div className="flex space-x-3">
            {/* 批量操作按鈕 */}
            {isAdmin(userRole) && (
              <MultiBulkActionButton
                selectedCount={selectedProjects.length}
                isDeleting={isBulkDeleting}
                onDelete={handleBulkDelete}
              />
            )}
            <button 
              onClick={() => canAddCustomer && handleAddProject()}
              disabled={!canAddCustomer}
              className={canAddCustomer ? btnPrimary : btnDisabled}
              title={canAddCustomer ? undefined : t('customers.list.limitReached', { ns: 'pages' })}
            >
              {t('projects.list.addButton', { ns: 'pages' })}
            </button>
          </div>
        </div>
      </div>

      {/* 客戶數量限制資訊 */}
      <CustomerLimitBanner 
        onLimitChange={setCanAddCustomer} 
        refreshTrigger={limitBannerRefreshTrigger}
      />

      {error && (
        <div className="mb-4 p-4 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}

      <div className="mb-4">
        <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('projects.list.searchPlaceholder', { ns: 'pages' })} />
      </div>

      {/* 表格上方的分頁控制 */}
      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {/* 骨架屏載入狀態 */}
      {listLoading ? (
        <TableSkeleton rows={10} columns={6} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <Table style={{ tableLayout: 'fixed', minWidth: '850px' }}>
            <TableHeader>
              <TableRow>
                <BulkSelectHeader
                  userRole={userRole}
                  allSelected={allSelected}
                  onToggleAll={toggleSelectAll}
                  hasItems={listItems.length > 0}
                />
                <TableHead className="w-44">{t('projects.form.name', { ns: 'pages' })}</TableHead>
                <TableHead className="w-36">{t('common.table.columns.customer')}</TableHead>
                <TableHead className="w-44">{t('common.table.columns.sourceQuote')}</TableHead>
                <TableHead className="w-28">{t('common.table.columns.status')}</TableHead>
                <TableHead className="w-44">{t('common.table.columns.dates')}</TableHead>
                <TableHead className="w-28">{t('common.table.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listItems.length === 0 ? (
                <ListEmptyRow
                  colSpan={7}
                  filtered={!!search}
                  title={t('projects.list.noProjects', { ns: 'pages' })}
                  hint={t('projects.list.emptyHint', { ns: 'pages' })}
                  action={canAddCustomer && (
                    <button type="button" onClick={handleAddProject} className={btnPrimary}>{t('projects.list.addButton', { ns: 'pages' })}</button>
                  )}
                />
              ) : (
                listItems.map((project) => (
                  <TableRow key={project.id} className={`${getArchivedRowClassName(project.customer)} ${pendingRowClass(project)}`}>
                    <BulkSelectCell
                      userRole={userRole}
                      isSelected={isItemSelected(project.id)}
                      onToggle={() => toggleItemSelection(project.id)}
                      canDelete={canDelete()}
                    />
                    <TableCell className="font-medium truncate">{project.name}</TableCell>
                    <TableCell className="truncate">
                      {project.customer?.name || <span className="text-fg-muted">—</span>}
                      {getCustomerStatus(project.customer).isArchived && (
                        <span className="ml-1 text-[13px] text-fg-secondary">({t('common.archived')})</span>
                      )}
                    </TableCell>
                    <TableCell className="truncate">
                      {project.quote?.title || <span className="text-fg-muted">—</span>}
                    </TableCell>
                    <TableCell>
                      <StatusDot tone={toneOf(projectTone, project.status)} label={t(`projects.status.${project.status}`, { ns: 'pages' })} />
                    </TableCell>
                    <TableCell className="truncate tabular-nums">
                      {project.startDate || project.endDate ? (
                        <>
                          {formatTableDate(project.startDate, i18n.language) || '—'}
                          <span className="mx-1 text-fg-muted">–</span>
                          {formatTableDate(project.endDate, i18n.language) || '—'}
                        </>
                      ) : (
                        <span className="text-fg-muted">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <RowActions>
                        {canModify(project, userRole, currentUserId) && (
                          <>
                            <RowAction onClick={() => handleEditProject(project)}>{t('common.actions.edit')}</RowAction>
                            <RowAction tone="danger" onClick={() => handleDelete(project.id)}>{t('common.actions.delete')}</RowAction>
                          </>
                        )}
                      </RowActions>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title={t('projects.list.confirmDeleteTitle', { ns: 'pages' })}
        message={t('projects.list.confirmDeleteMessage', { ns: 'pages' })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <ProjectDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        project={selectedProject}
      />

      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={closeBulkDeleteModal}
        onConfirm={confirmBulkDelete}
        title={t('common.bulk.confirmDeleteTitle', { count: selectedProjects.length, entity: t('projects.item', { ns: 'pages' }) })}
        message={t('common.bulk.confirmDeleteMessage', { count: selectedProjects.length, entity: t('projects.item', { ns: 'pages' }) })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isBulkDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />
    </div>
  );
};

export default ProjectListPage;